/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { ReactNode } from 'react';

/**
 * Convert newline to break tag
 *
 * @param   {string}  str                   The string
 * @return  {'' | (string | JSX.Element)[]} The output
 */

export function nl2br(str: string | null): ReactNode {
  if (typeof str === 'undefined' || str === null || str === '') {
    return '';
  }
  const regex = /([^>\r\n]?)(\r\n|\n\r|\r|\n)/g;
  const breakTag = <br />;
  return (str + '').split(regex).map((txt, i) => {
    if (regex.test(txt)) {
      return <React.Fragment key={`nl2br_${str}_${i}`}>{breakTag}{txt}</React.Fragment>
    }
    return txt;
  });
}

export const naturalSort = (new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })).compare;

export function capitalise(input: string): string {
  return input && input.replace(/\b\w/g, l => l.toUpperCase());
}

export const capitaliseFirstLetter = (input: string): string => {
  return input.charAt(0).toUpperCase() + input.slice(1);
};

export function bytesToHumanReadable(value: number): string | number {
  const conversions = [
    {
      factor: 1048576,
      postfix: 'kb'
    },
    {
      factor: 1073741824,
      postfix: 'Mb'
    },
    {
      factor: 1073741824 * 1024,
      postfix: 'Gb'
    },
    {
      factor: 1073741824 * 1048576,
      postfix: 'Tb'
    },
  ];

  const conversion = conversions.find(({ factor }) => (value < factor));
  if (conversion) {
    return `${Number(value / (conversion.factor / 1024)).toFixed(1)}${conversion.postfix}`;
  }
  return value;
}

export function replaceUnderscores(input: string): string {
  return input && input.replace(/_/g, ' ');
}

export function ellipsis(input: string | undefined, len: number = 30): string {
  if (!input) {
    return '';
  }
  return input.length > len ? `${input.trim().substring(0, len)}...` : input;
}

// from https://stackoverflow.com/questions/7128675/from-green-to-red-color-depend-on-percentage
export function getColor(value: number): string {
  return hslToRgb(Math.min(value, 1) * .3, .8, .4);
}

// from https://stackoverflow.com/questions/2353211/hsl-to-rgb-color-conversion

/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * @param   {number}  h       The hue
 * @param   {number}  s       The saturation
 * @param   {number}  l       The lightness
 * @return  {string}          The RGB representation
 */

export function hslToRgb(h: number, s: number, l: number): string {
  let r, g, b;
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return 'rgb(' + Math.round(r * 255) + ',' + Math.round(g * 255) + ',' + Math.round(b * 255) + ')';
}

function hue2rgb(p: number, q: number, t: number): number {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

export const getSuffix = ({ valueType }: { valueType: string }): string => {
  switch (valueType) {
    case 'percentage':
      return '%';
    default:
      return '';
  }
};

export const isValidEmail = (email: string | undefined): boolean => {
  //const EMAIL_REGEXP = /^[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;
  const EMAIL_REGEXP = /^(([^<>()\]\\.,;:\s@"]+(\.[^<>()\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
  return EMAIL_REGEXP.test(email as string);
};

export const generateId = () => '_' + Math.random().toString(36).slice(2, 11);

// CSS
export const MD_BREAKPOINT = 768;
export const LG_BREAKPOINT = 992;

export function isDefined<T = number>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}
